import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, FileText, Eye, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EmailTemplates = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [previewTemplate, setPreviewTemplate] = useState(null);

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/email/templates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTemplates(response.data);
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  };

  const handleInitialize = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/email/templates/initialize`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message);
      fetchTemplates();
    } catch (error) {
      console.error('Error initializing templates:', error);
      toast.error('Failed to initialize templates');
    } finally {
      setLoading(false);
    }
  };

  const templateIcons = {
    'monthly_magazine': '📰',
    'expiry_30_days': '⏰',
    'expiry_15_days': '⚠️',
    'expiry_7_days': '🚨'
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-purple-50 py-8">
      <div className="max-w-6xl mx-auto px-6">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center space-x-4">
            <Button variant="outline" onClick={() => navigate('/')}>
              <ArrowLeft className="mr-2" size={18} />
              Back
            </Button>
            <h1 className="text-4xl font-bold" style={{ color: '#7c3aed' }}>📋 Email Templates</h1>
          </div>
          {templates.length === 0 && (
            <Button onClick={handleInitialize} disabled={loading} style={{ backgroundColor: '#059669' }}>
              <Plus className="mr-2" size={18} />
              {loading ? 'Initializing...' : 'Initialize Templates'}
            </Button>
          )}
        </div>

        {templates.length === 0 ? (
          <div className="form-section text-center py-12">
            <FileText size={64} className="mx-auto mb-4 text-gray-400" />
            <h2 className="text-2xl font-bold mb-2">No Templates Found</h2>
            <p className="text-gray-600 mb-6">Click the button above to create 4 default email templates</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {templates.map((template) => (
              <div key={template.id} className="form-section hover:shadow-lg transition-shadow">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <div className="flex items-center space-x-2 mb-2">
                      <span className="text-3xl">{templateIcons[template.template_type] || '📧'}</span>
                      <h3 className="text-xl font-bold" style={{ color: '#7c3aed' }}>{template.template_name}</h3>
                    </div>
                    <p className="text-sm text-gray-600"><strong>Subject:</strong> {template.subject}</p>
                  </div>
                </div>
                <div className="mb-4">
                  <p className="text-xs text-gray-500 mb-2"><strong>Variables:</strong></p>
                  <div className="flex flex-wrap gap-2">
                    {template.variables.map((v) => (
                      <span key={v} className="px-2 py-1 bg-purple-100 text-purple-700 rounded text-xs">
                        {`{{${v}}}`}
                      </span>
                    ))}
                  </div>
                </div>
                <Button onClick={() => setPreviewTemplate(template)} variant="outline" className="w-full">
                  <Eye className="mr-2" size={16} />
                  Preview Template
                </Button>
              </div>
            ))}
          </div>
        )}

        {previewTemplate && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={() => setPreviewTemplate(null)}>
            <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
              <div className="p-6">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-2xl font-bold" style={{ color: '#7c3aed' }}>{previewTemplate.template_name}</h2>
                  <Button variant="outline" onClick={() => setPreviewTemplate(null)}>Close</Button>
                </div>
                <div className="mb-4 p-4 bg-gray-100 rounded">
                  <p className="text-sm"><strong>Subject:</strong> {previewTemplate.subject}</p>
                </div>
                <div className="border p-4 rounded" dangerouslySetInnerHTML={{ __html: previewTemplate.html_content.replace(/{{(\w+)}}/g, '<span class="bg-yellow-200 px-1">$1</span>') }} />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EmailTemplates;